import { COVERAGE_BETA_MAX_IMAGE_ZOOM } from "@/config/coverage-beta-zoom";
import { ReferenceImageMatch } from "@/dev/sample-out-of-stock-predictions";
import { useFetchGalleryImgUrl } from "@/hooks/useFetchGalleryImgUrl";
import { captureImageLoad } from "@/utils/captureImageLoad";
import { setBoxOutlineZoom } from "@/utils/coverage-beta-box-outline";
import { useCoverageBetaStore } from "@/store/useCoverageBetaStore";
import { usePostHog } from "@posthog/react";
import { formatRelativeAge } from "@/utils/utils";
import { ImageContextSchema } from "@augmodo/lebron-browser-client";
import { IconClock, IconSun } from "@tabler/icons-react";
import { useRef, useState, useEffect } from "react";
import {
  TransformComponent,
  TransformWrapper,
  type ReactZoomPanPinchContentRef,
} from "react-zoom-pan-pinch";
import { ReferenceProductDetectionBox } from "./ReferenceProductDetectionBox";

interface Props {
  reference: ImageContextSchema;
  refImageMatches: ReferenceImageMatch[];
  isVisible: boolean;
  referenceImgIdx: number;
  queryTimestamp?: string;
  /**
   * Fired whenever this reference image is both visible and settled (painted,
   * or failed to load — see `settled`) — i.e.
   * whichever reference is currently active. ShelfImagesView wires this to
   * every reference instance; only the one that's actually showing ever
   * calls it. CoverageBetaTask uses it (alongside the query image's own
   * onLoad) as the "main data is loaded" signal that unlocks the planogram.
   */
  onVisibleLoad?: () => void;
}

export const ReferenceImage = (props: Props) => {
  const {
    reference,
    refImageMatches,
    isVisible,
    referenceImgIdx,
    queryTimestamp,
    onVisibleLoad,
  } = props;
  const referenceImageId = reference?.image_id ?? "";
  const { data: referenceImgResp } = useFetchGalleryImgUrl(referenceImageId);
  const referenceImgObjUrl = referenceImgResp?.objUrl || "";
  const referenceProductDetections = reference?.product_detections ?? [];
  const showBoxes = useCoverageBetaStore((s) => s.showBoxes);
  const isReadOnly = useCoverageBetaStore((s) => s.isReadOnly);
  const setReferencePinchRef = useCoverageBetaStore(
    (s) => s.setReferencePinchRef,
  );
  const posthog = usePostHog();

  const relativeAge = formatRelativeAge(
    referenceImgResp?.timestamp,
    queryTimestamp,
  );

  const contentRef = useRef<HTMLDivElement>(null);
  const transformRef = useRef<ReactZoomPanPinchContentRef>(null);
  // True once the <img> has either painted or errored. Treating an error as
  // settled keeps a broken/expired image from stranding the task: its boxes
  // still render (so products stay selectable) and warm-up still fires.
  const [settled, setSettled] = useState(false);

  const firstReferenceImageMatchProductIdx =
    refImageMatches?.[0]?.product_index ?? -1;

  useEffect(() => {
    if (!isVisible || !settled) return;
    const el = contentRef.current?.querySelector<HTMLElement>(
      `[data-product-index="${firstReferenceImageMatchProductIdx}"]`,
    );
    if (el) {
      transformRef.current?.zoomToElement(el, 3, 0);
    } else {
      transformRef.current?.resetTransform(0);
    }
  }, [isVisible, settled, firstReferenceImageMatchProductIdx]);

  useEffect(() => {
    if (isVisible && settled) onVisibleLoad?.();
  }, [isVisible, settled, onVisibleLoad]);

  // Expose this (active) reference image's pan/zoom controller so the zoom + reset
  // hotkeys can act on it when the mouse is over the reference view. Clear it on
  // hide/unmount so a stale (hidden) image can't be driven.
  useEffect(() => {
    if (!isVisible) return;
    setReferencePinchRef(transformRef.current);
    return () => setReferencePinchRef(null);
  }, [isVisible, setReferencePinchRef]);

  return (
    <div ref={contentRef} className="size-full border relative bg-gray-300">
      {!!relativeAge && (
        <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 rounded-md border border-gray-300 bg-gray-100/95 px-3 py-1.5 text-sm font-semibold text-gray-700 shadow-md">
          <IconClock size={16} stroke={2.5} />
          {relativeAge}
        </div>
      )}
      {/* Leaf subscriber so a brightness step doesn't re-render this whole
          (box-heavy) image component — only the tiny badge re-renders. */}
      <ReferenceBrightnessBadge />
      <TransformWrapper
        ref={transformRef}
        initialScale={1}
        minScale={1}
        maxScale={COVERAGE_BETA_MAX_IMAGE_ZOOM}
        centerZoomedOut={false}
        limitToBounds={false}
        velocityAnimation={{ disabled: true }}
        doubleClick={{ disabled: true }}
        panning={{ velocityDisabled: true }}
        // Sizes the box outlines (see coverage-beta-box-outline). contentRef is
        // the view root, so the scale survives a change of reference image.
        onTransformed={(_ref, state) =>
          setBoxOutlineZoom(contentRef.current, state.scale)
        }
      >
        <TransformComponent
          wrapperStyle={{ width: "100%", height: "100%" }}
          contentStyle={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!!referenceImgObjUrl && (
            <div className="relative inline-block h-full">
              {/* Leaf subscriber for brightness — see ReferenceBrightnessBadge. */}
              <ReferenceImg
                src={referenceImgObjUrl}
                isVisible={isVisible}
                onLoad={() => {
                  setSettled(true);
                  if (!isReadOnly)
                    captureImageLoad(
                      posthog,
                      "reference",
                      referenceImgObjUrl,
                      referenceImgResp?.optimized,
                    );
                }}
                onError={() => setSettled(true)}
              />

              {/* isVisible && settled (not just showBoxes): an inactive reference
                  image is still mounted (see ShelfImagesView) so its <img> can
                  preload at low priority, but rendering its boxes would fire
                  one product-metadata request per detection for a shelf image
                  the labeler isn't looking at — across every reference image,
                  that's the request flood that starved the query image on
                  load. Gating on `settled` also delays the *visible* image's
                  own boxes (and their requests) until its bytes have painted
                  (or failed), instead of racing them. */}
              {showBoxes &&
                isVisible &&
                settled &&
                referenceProductDetections.map(
                  (productDetection: any, referenceProductDetectionIdx) => {
                    const isMatchingProduct = !!refImageMatches.find(
                      (i) => i.product_index === referenceProductDetectionIdx,
                    );
                    return (
                      <ReferenceProductDetectionBox
                        key={productDetection.id}
                        productDetection={productDetection}
                        referenceProductDetectionIdx={
                          referenceProductDetectionIdx
                        }
                        reference={reference}
                        isMatchingProduct={isMatchingProduct}
                        referenceImgIdx={referenceImgIdx}
                      />
                    );
                  },
                )}
            </div>
          )}
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
};

/**
 * The reference <img> as a leaf that subscribes to brightness on its own, so a
 * brightness hotkey step re-renders only this element — not the parent
 * ReferenceImage and its (potentially dozens of) detection boxes. Without this,
 * every mounted reference image re-rendered on each step, making the reference
 * view noticeably laggier than the single query image.
 */
const ReferenceImg = ({
  src,
  isVisible,
  onLoad,
  onError,
}: {
  src: string;
  isVisible: boolean;
  onLoad: () => void;
  onError: () => void;
}) => {
  const brightness = useCoverageBetaStore((s) => s.brightnessByView.reference);
  return (
    <img
      src={src}
      className="h-full w-auto"
      style={{ filter: `brightness(${brightness}%)` }}
      // Only the visible reference competes for bandwidth up front; the rest
      // load at low priority behind it.
      fetchpriority={isVisible ? "high" : "low"}
      onLoad={onLoad}
      onError={onError}
    />
  );
};

/** Small "☀ N%" badge shown when the reference brightness is off 100%. Its own
 * leaf subscriber for the same reason as ReferenceImg. */
const ReferenceBrightnessBadge = () => {
  const brightness = useCoverageBetaStore((s) => s.brightnessByView.reference);
  if (brightness === 100) return null;
  return (
    <div className="absolute bottom-3 left-3 z-10 flex items-center gap-1.5 rounded-md border border-gray-300 bg-gray-100/95 px-2 py-1 text-xs font-semibold text-gray-700 shadow-md">
      <IconSun size={14} stroke={2.5} />
      {brightness}%
    </div>
  );
};
